import { useEffect, useMemo, useState } from "react";
import { desktopOnly } from "@/components/phone/DesktopOnlyNote";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertTriangle, CheckCircle2, Loader2, Mail, MessageSquare, Pencil, RotateCcw, Save, Send, XCircle } from "lucide-react";
import { toast } from "sonner";
import { getAircallUKSmsLines, getPrechaseFacts, getPrechaseTemplates, previewPrechase, savePrechaseTemplates, sendPrechase } from "@/lib/api";
import { FilterBuilder, applyRules, baseSf } from "./PreChaseFilter";
import { QuietTag } from "./AwaitingPaymentTable";
import { ChasedPill, DpdPill, LegalPill, RestructuredPill } from "./pills";

const gbp = (v) => new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(v || 0);
const LINE_KEY = "sf_prechase_line_id";
const dmy = (iso) => (iso ? iso.slice(8, 10) + "/" + iso.slice(5, 7) + "/" + iso.slice(2, 4) : "—");

function LoanTable({ loans, chased, facts, selected, setSelected }) {
  const pickable = loans.filter((l) => !chased[baseSf(l.sf_number)]);
  const allOn = pickable.length > 0 && pickable.every((l) => selected.has(l.sf_number));
  const toggleAll = () => setSelected((s) => { const n = new Set(s); pickable.forEach((l) => (allOn ? n.delete(l.sf_number) : n.add(l.sf_number))); return n; });
  const toggle = (sf) => setSelected((s) => { const n = new Set(s); n.has(sf) ? n.delete(sf) : n.add(sf); return n; });
  return (
    <div className="max-h-[360px] overflow-y-auto rounded-md border border-slate-200" data-testid="prechase-loan-list">
      <table className="w-full text-sm">
        <thead className="sticky top-0 z-10 border-b bg-slate-50 text-[10px] uppercase text-muted-foreground">
          <tr className="[&>th]:px-3 [&>th]:py-2 [&>th]:text-left [&>th]:font-semibold">
            <th className="w-8"><Checkbox checked={allOn} onCheckedChange={toggleAll} aria-label="Select all" data-testid="prechase-select-all" /></th>
            <th>Borrower</th><th>SF</th><th className="!text-right">Due</th><th>Arrears</th><th>Disbursed</th><th>Contact</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {loans.map((l) => {
            const f = facts?.[l.sf_number];
            const done = chased[baseSf(l.sf_number)]?.[0];
            return (
              <tr key={l.sf_number} className={done ? "opacity-50" : "hover:bg-emerald-50/40 cursor-pointer"} onClick={() => !done && toggle(l.sf_number)} data-testid={`prechase-loan-${l.sf_number}`}>
                <td className="px-3 py-1.5" onClick={(e) => e.stopPropagation()}><Checkbox checked={selected.has(l.sf_number)} disabled={!!done} onCheckedChange={() => toggle(l.sf_number)} data-testid={`prechase-check-${l.sf_number}`} /></td>
                <td className="px-3 py-1.5 font-medium text-slate-900">
                  <span className="inline-flex flex-wrap items-center gap-1.5">
                    {l.borrower_name}
                    {f?.legal && <LegalPill handler={f.handler} />}
                    {done && <ChasedPill channel={done.channel} testId={`prechase-chased-${l.sf_number}`} />}
                  </span>
                </td>
                <td className="px-3 py-1.5 font-mono text-xs text-slate-500 whitespace-nowrap"><span className="inline-flex items-center gap-1.5">{l.sf_number}{f?.restructured && <RestructuredPill iteration={f.iteration} testId={`prechase-restructured-${l.sf_number}`} />}</span></td>
                <td className="px-3 py-1.5 text-right font-mono tabular-nums font-semibold">{gbp(l.due_amount)}</td>
                <td className="px-3 py-1.5 text-xs">{f?.in_arrears ? <span className="inline-flex items-center gap-1.5 text-rose-700 font-medium tabular-nums">{gbp(f.arrears_amount)}{f.days_past_due > 0 && <DpdPill dpd={f.days_past_due} />}</span> : <span className="text-slate-300">—</span>}</td>
                <td className="px-3 py-1.5 text-xs text-slate-500 font-mono">{f ? dmy(f.released_date) : <span className="text-slate-300">…</span>}</td>
                <td className="px-3 py-1.5">
                  <span className="flex items-center gap-1.5">
                    <MessageSquare className={`w-3.5 h-3.5 ${l.mobile ? "text-emerald-600" : "text-slate-200"}`} />
                    <Mail className={`w-3.5 h-3.5 ${l.email ? "text-emerald-600" : "text-slate-200"}`} />
                  </span>
                </td>
              </tr>
            );
          })}
          {!loans.length && <tr><td colSpan={7} className="py-8 text-center text-sm text-slate-400">No loans match the filter.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function MessageStep({ channel, setChannel, lines, lineId, setLineId, tpl, setTpl, placeholders, onSaveDefault, saving }) {
  const body = channel === "sms" ? tpl.sms : tpl.email_body;
  const setBody = (v) => setTpl((t) => (channel === "sms" ? { ...t, sms: v } : { ...t, email_body: v }));
  const insert = (p) => setBody(`${body || ""}${body && !body.endsWith(" ") ? " " : ""}${p}`);
  return (
    <div className="space-y-3">
      <div className="flex gap-2" data-testid="prechase-channel-toggle">
        {[["sms", "SMS via Aircall", MessageSquare], ["email", "Email from accounts@", Mail]].map(([k, label, Icon]) => (
          <Button key={k} variant={channel === k ? "default" : "outline"} size="sm" className={channel === k ? "bg-emerald-600 hover:bg-emerald-700" : ""} onClick={() => setChannel(k)} data-testid={`prechase-channel-${k}`}>
            <Icon className="w-4 h-4 mr-2" />{label}
          </Button>
        ))}
      </div>
      {channel === "sms" && (
        <Select value={lineId ? String(lineId) : ""} onValueChange={(v) => setLineId(Number(v))}>
          <SelectTrigger className="h-9 text-sm" data-testid="prechase-line-select"><SelectValue placeholder="Send from Aircall line…" /></SelectTrigger>
          <SelectContent>
            {lines.map((l) => (
              <SelectItem key={l.id} value={String(l.id)} disabled={!l.public_api_sms} data-testid={`prechase-line-${l.id}`}>
                {l.name} · {l.digits}{l.public_api_sms ? "" : " (SMS API not enabled)"}
              </SelectItem>
            ))}
            {!lines.length && <div className="px-3 py-2 text-xs text-muted-foreground">No UK Aircall lines found</div>}
          </SelectContent>
        </Select>
      )}
      {channel === "email" && (
        <Input value={tpl.email_subject || ""} onChange={(e) => setTpl((t) => ({ ...t, email_subject: e.target.value }))} placeholder="Subject" className="h-9 text-sm" data-testid="prechase-subject" />
      )}
      <Textarea value={body || ""} onChange={(e) => setBody(e.target.value)} rows={channel === "sms" ? 5 : 9} className="text-sm" data-testid="prechase-body" />
      <div className="flex flex-wrap items-center gap-1.5">
        {placeholders.map((p) => (
          <button key={p} type="button" onClick={() => insert(p)} className="rounded border border-slate-200 bg-slate-50 px-2 py-0.5 font-mono text-[11px] text-slate-600 hover:bg-slate-100" data-testid={`prechase-placeholder-${p.replace(/[{}]/g, "")}`}>{p}</button>
        ))}
        <span className="ml-auto text-xs text-muted-foreground" data-testid="prechase-char-count">
          {channel === "sms" ? `${(body || "").length} chars · ~${Math.max(1, Math.ceil((body || "").length / 153))} segment(s) before merge fields` : "Collections Team sign-off is added automatically"}
        </span>
      </div>
      <Button variant="ghost" size="sm" onClick={onSaveDefault} disabled={saving} data-testid="prechase-save-default">
        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Save as team default
      </Button>
    </div>
  );
}

function ReviewRow({ row, channel, override, setOverride, onCommit }) {
  const [editing, setEditing] = useState(false);
  const edited = !!override;
  const start = () => { setOverride({ body: row.body, subject: row.subject || "" }); setEditing(true); };
  return (
    <div className={`px-3 py-2 text-sm ${row.skip ? "bg-slate-50 opacity-70" : ""}`} data-testid={`prechase-review-${row.sf_number}`}>
      <div className="flex items-center gap-2">
        <span className="font-medium text-slate-900">{row.borrower_name}</span>
        <span className="font-mono text-xs text-slate-500">{row.sf_number} · {gbp(row.due_amount)}</span>
        {edited && <QuietTag tone="emerald" testId={`prechase-review-edited-badge-${row.sf_number}`}>Edited</QuietTag>}
        <span className="ml-auto font-mono text-xs text-slate-500">{row.to || "—"}</span>
        {!row.skip && !editing && (
          <Button variant="ghost" size="sm" className="h-6 px-2 text-xs text-slate-500 hover:text-emerald-700" onClick={start} data-testid={`prechase-review-edit-btn-${row.sf_number}`}><Pencil className="w-3 h-3 mr-1" />Edit</Button>
        )}
        {edited && (
          <Button variant="ghost" size="sm" className="h-6 px-2 text-xs text-slate-500" onClick={() => { setOverride(null); setEditing(false); onCommit(null); }} data-testid={`prechase-review-revert-btn-${row.sf_number}`}><RotateCcw className="w-3 h-3 mr-1" />Revert</Button>
        )}
      </div>
      {row.skip ? (
        <div className="mt-1 flex items-center gap-1 text-xs text-amber-700"><AlertTriangle className="w-3.5 h-3.5" />Skipped — {row.skip}</div>
      ) : editing ? (
        <div className="mt-2 space-y-1.5">
          {channel === "email" && <Input value={override?.subject || ""} onChange={(e) => setOverride({ ...override, subject: e.target.value })} className="h-8 text-xs" data-testid={`prechase-review-subject-${row.sf_number}`} />}
          <Textarea value={override?.body || ""} onChange={(e) => setOverride({ ...override, body: e.target.value })} rows={channel === "sms" ? 4 : 7} className="text-xs" data-testid={`prechase-review-textarea-${row.sf_number}`} />
          <div className="flex justify-end"><Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setEditing(false); onCommit(override); }} data-testid={`prechase-review-done-btn-${row.sf_number}`}>Done</Button></div>
        </div>
      ) : (
        <div className="mt-1 whitespace-pre-wrap text-xs text-slate-600" data-testid={`prechase-review-text-${row.sf_number}`}>
          {row.subject ? <div className="font-semibold">{row.subject}</div> : null}{row.body}
        </div>
      )}
    </div>
  );
}

function ReviewStep({ preview, channel, overrides, setOverrides, onRefresh }) {
  if (!preview) return <div className="py-8 text-center text-sm text-muted-foreground"><Loader2 className="w-5 h-5 mx-auto animate-spin" /></div>;
  return (
    <div className="space-y-2">
      <div className="text-sm" data-testid="prechase-review-summary">
        <span className="font-semibold text-emerald-700">{preview.sendable}</span> will be sent by {channel === "sms" ? "SMS" : "email"}
        {preview.skipped ? <span className="text-muted-foreground"> · {preview.skipped} skipped</span> : null}
        <span className="text-muted-foreground"> · edit any message before sending — edits apply to that recipient only</span>
      </div>
      <div className="max-h-[380px] overflow-y-auto divide-y rounded-md border">
        {preview.rows.map((r) => (
          <ReviewRow key={r.sf_number} row={r} channel={channel} override={overrides[r.sf_number]}
            setOverride={(o) => setOverrides((cur) => { const n = { ...cur }; if (o) n[r.sf_number] = o; else delete n[r.sf_number]; return n; })}
            onCommit={(o) => { const n = { ...overrides }; if (o) n[r.sf_number] = o; else delete n[r.sf_number]; onRefresh(n); }} />
        ))}
      </div>
    </div>
  );
}

function ResultStep({ result }) {
  const Row = ({ icon: Icon, tone, title, rows, render }) => (
    rows.length ? (
      <div>
        <div className={`flex items-center gap-1.5 text-sm font-semibold ${tone}`}><Icon className="w-4 h-4" />{title} ({rows.length})</div>
        <ul className="mt-1 ml-5 list-disc text-xs text-slate-600">{rows.map((r, i) => <li key={i}>{render(r)}</li>)}</ul>
      </div>
    ) : null
  );
  return (
    <div className="space-y-3" data-testid="prechase-result">
      <Row icon={CheckCircle2} tone="text-emerald-700" title={result.dry_run ? "Would send" : "Sent"} rows={result.sent}
        render={(r) => `${r.borrower_name} (${r.sf_number}) → ${r.to}${r.case_created ? " · new Portfolio Account file created" : ""}`} />
      <Row icon={AlertTriangle} tone="text-amber-700" title="Skipped" rows={result.skipped} render={(r) => `${r.borrower_name} (${r.sf_number}) — ${r.reason}`} />
      <Row icon={XCircle} tone="text-red-700" title="Failed" rows={result.failed} render={(r) => `${r.borrower_name} (${r.sf_number}) — ${r.error}`} />
      {!result.sent.length && !result.skipped.length && !result.failed.length && <p className="text-sm text-muted-foreground">Nothing to do.</p>}
    </div>
  );
}

function PreChaseWizard({ open, onOpenChange, loans, date, chased, facts: factsProp, onSent }) {
  const [step, setStep] = useState(0);
  const [rules, setRules] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [channel, setChannel] = useState("sms");
  const [tpl, setTpl] = useState({ sms: "", email_subject: "", email_body: "" });
  const [placeholders, setPlaceholders] = useState([]);
  const [lines, setLines] = useState([]);
  const [lineId, setLineId] = useState(() => Number(localStorage.getItem(LINE_KEY)) || null);
  const [facts, setFacts] = useState(factsProp || null);
  const [factsLoading, setFactsLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [overrides, setOverrides] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (factsProp) setFacts(factsProp); }, [factsProp]);

  useEffect(() => {
    if (!open) return;
    setStep(0); setSelected(new Set()); setPreview(null); setResult(null); setOverrides({});
    getPrechaseTemplates().then((t) => {
      setTpl({ sms: t.sms, email_subject: t.email_subject, email_body: t.email_body });
      setPlaceholders(t.placeholders || []);
      if (!Number(localStorage.getItem(LINE_KEY)) && t.line_id) setLineId(t.line_id);
    }).catch(() => toast.error("Could not load pre-chase templates"));
    getAircallUKSmsLines().then((r) => setLines(r.lines || [])).catch(() => setLines([]));
    if (!factsProp && loans.length) {
      setFactsLoading(true);
      getPrechaseFacts({ date, loans }).then((r) => setFacts(r.facts || {})).catch(() => setFacts({})).finally(() => setFactsLoading(false));
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (lineId) localStorage.setItem(LINE_KEY, String(lineId)); }, [lineId]);

  const filtered = useMemo(() => applyRules(rules, loans, facts, chased), [rules, loans, facts, chased]);
  const chosen = useMemo(() => loans.filter((l) => selected.has(l.sf_number)), [loans, selected]);
  const payload = (ov = overrides) => ({
    date, channel, body: channel === "sms" ? tpl.sms : tpl.email_body, subject: channel === "email" ? tpl.email_subject : undefined,
    loans: chosen.map((l) => ({ loan_id: l.loan_id, borrower_id: l.borrower_id, sf_number: l.sf_number, borrower_name: l.borrower_name, borrower_first_name: l.borrower_first_name,
      due_amount: l.due_amount, due_date: l.due_date, mobile: l.mobile, email: l.email, principal_balance: l.principal_balance,
      body_override: ov[l.sf_number]?.body || undefined, subject_override: channel === "email" ? ov[l.sf_number]?.subject || undefined : undefined })),
  });

  const goReview = async () => {
    if (channel === "sms" && !lineId) return toast.error("Pick the Aircall line to send from");
    setBusy(true);
    try { setPreview(await previewPrechase(payload())); setStep(2); }
    catch (e) { toast.error(e?.response?.data?.detail || "Preview failed"); }
    finally { setBusy(false); }
  };

  const refreshPreview = async (ov) => {
    try { setPreview(await previewPrechase(payload(ov))); }
    catch (e) { toast.error(e?.response?.data?.detail || "Preview failed"); }
  };

  const doSend = async () => {
    setBusy(true);
    try {
      const r = await sendPrechase({ ...payload(), line_id: channel === "sms" ? lineId : undefined });
      setResult(r); setStep(3);
      if (r.counts.sent) toast.success(`${r.counts.sent} pre-chase ${channel === "sms" ? "SMS" : "email"}${r.counts.sent > 1 ? "s" : ""} sent and logged`);
      if (r.counts.failed) toast.error(`${r.counts.failed} failed`);
      onSent?.(r);
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    finally { setBusy(false); }
  };

  const saveDefault = async () => {
    setSaving(true);
    try { await savePrechaseTemplates({ sms: tpl.sms, email_subject: tpl.email_subject, email_body: tpl.email_body, line_id: lineId || undefined }); toast.success("Saved as the team default"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  const editedCount = Object.keys(overrides).length;
  const titles = ["1 · Filter & pick the loans", "2 · Message", "3 · Review & send", "Done"];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl overflow-hidden" data-testid="prechase-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-900"><Send className="w-5 h-5 text-emerald-600" />Pre-chase · Awaiting payment</DialogTitle>
          <DialogDescription data-testid="prechase-step-title">{titles[step]} · one reminder per loan per day · logged on the customer file</DialogDescription>
        </DialogHeader>
        {step === 0 && (
          <div className="space-y-2">
            <FilterBuilder rules={rules} setRules={setRules} matchCount={filtered.length} totalCount={loans.length} factsLoading={factsLoading} />
            <LoanTable loans={filtered} chased={chased} facts={facts} selected={selected} setSelected={setSelected} />
          </div>
        )}
        {step === 1 && <MessageStep channel={channel} setChannel={setChannel} lines={lines} lineId={lineId} setLineId={setLineId} tpl={tpl} setTpl={setTpl} placeholders={placeholders} onSaveDefault={saveDefault} saving={saving} />}
        {step === 2 && <ReviewStep preview={preview} channel={channel} overrides={overrides} setOverrides={setOverrides} onRefresh={refreshPreview} />}
        {step === 3 && result && <ResultStep result={result} />}
        <div className="flex items-center justify-between pt-2">
          <span className="text-xs text-muted-foreground" data-testid="prechase-selected-count">
            {chosen.length} loan{chosen.length === 1 ? "" : "s"} selected · {gbp(chosen.reduce((s, l) => s + (l.due_amount || 0), 0))}{editedCount ? ` · ${editedCount} edited` : ""}
          </span>
          <div className="flex gap-2">
            {step > 0 && step < 3 && <Button variant="outline" size="sm" onClick={() => setStep(step - 1)} disabled={busy} data-testid="prechase-back">Back</Button>}
            {step === 0 && <Button size="sm" onClick={() => setStep(1)} disabled={!chosen.length} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="prechase-next">Next</Button>}
            {step === 1 && <Button size="sm" onClick={goReview} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="prechase-next">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Review"}</Button>}
            {step === 2 && <Button size="sm" onClick={doSend} disabled={busy || !preview?.sendable} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="prechase-send">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}Send {preview?.sendable || 0} {channel === "sms" ? "SMS" : "email"}{preview?.sendable === 1 ? "" : "s"}
            </Button>}
            {step === 3 && <Button size="sm" onClick={() => onOpenChange(false)} data-testid="prechase-done">Close</Button>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default desktopOnly(PreChaseWizard, "The pre-chase wizard");
